import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import AdminTopNavbar from './components/AdminTopNavbar';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { getAllCardsAdmin, updateCardAdmin, deleteCardAdmin, getActiveTransactions, getTotalRevenue } from '../api/adminApi';

const CardManagement = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [cards, setCards] = useState([]);
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cardsData, transactionsData, revenueData] = await Promise.all([
        getAllCardsAdmin(),
        getActiveTransactions(),
        getTotalRevenue()
      ]);
      setCards(cardsData);
      setTotalItems(cardsData.length);
      setActiveTransactions(transactionsData);
      setTotalRevenue(revenueData.totalRevenue || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      toast.error(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination values
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleStatusChange = async (cardId, newStatus) => {
    try {
      await updateCardAdmin(cardId, { status: newStatus });
      setCards(cards.map(card => 
        card.id === cardId ? { ...card, status: newStatus } : card
      ));
      toast.success(`Card status updated to ${newStatus}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update card status');
    }
  };

  const handleDeleteClick = (card) => {
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (cardToDelete) {
      try {
        await deleteCardAdmin(cardToDelete._id);
        setCards(cards.filter(card => card._id !== cardToDelete._id));
        toast.success('Card deleted successfully');
      } catch (err) {
        console.error('Error deleting card:', err);
        toast.error(err.message || 'Failed to delete card');
      } finally {
        setShowDeleteModal(false);
        setCardToDelete(null);
      }
    }
  };

  // Filter cards based on search term and filters
  const filteredCards = cards.filter(card => {
    const matchesSearch = card?.cardTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
    const matchesCategory = categoryFilter === 'all' || card?.category?.includes(categoryFilter);
    const matchesRarity = rarityFilter === 'all' || card?.rarity === rarityFilter;
    return matchesSearch && matchesCategory && matchesRarity;
  });

  // Get current cards for pagination
  const currentCards = filteredCards.slice(indexOfFirstItem, indexOfLastItem);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <AdminTopNavbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminTopNavbar />

      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Card Management</h1>
            <p className="text-gray-400">Manage platform cards and their properties</p>
          </div>
          <button
            onClick={() => window.location.href = '/admin/trending-cards'}
            className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold px-6 py-3 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center space-x-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus">
              <path d="M5 12h14"/>
              <path d="M12 5v14"/>
            </svg>
            <span>Add Trending Card</span>
          </button>
        </div>
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg relative mb-4 backdrop-blur-sm" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl p-4 flex items-center border border-gray-700/50">
            <div className="p-3 rounded-full bg-red-500/20 text-red-400 mr-4">
              <i className="bi bi-card-text text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Cards</p>
              <h3 className="text-xl font-semibold text-white">{cards.length}</h3>
            </div>
          </div>
          <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl p-4 flex items-center border border-gray-700/50">
            <div className="p-3 rounded-full bg-green-500/20 text-green-400 mr-4">
              <i className="bi bi-check-circle text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-400">Active Cards</p>
              <h3 className="text-xl font-semibold text-white">
                {activeTransactions}
              </h3>
            </div>
          </div>
          <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl p-4 flex items-center border border-gray-700/50">
            <div className="p-3 rounded-full bg-yellow-500/20 text-yellow-400 mr-4">
              <i className="bi bi-currency-rupee text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Price</p>
              <h3 className="text-xl font-semibold text-white">
              ₹{cards.reduce((acc, curr) => acc + curr.price, 0).toLocaleString()}
              </h3>
            </div>
          </div>
          <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl p-4 flex items-center border border-gray-700/50">
            <div className="p-3 rounded-full bg-blue-500/20 text-blue-400 mr-4">
              <i className="bi bi-currency-rupee text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Revenue</p>
              <h3 className="text-xl font-semibold text-white">
              ₹{totalRevenue.toLocaleString()}
              </h3>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl p-4 mb-6 border border-gray-700/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                placeholder="Search cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-2.5 text-gray-400">
                <i className="bi bi-search"></i>
              </div>
            </div>
            <select
              className="w-full px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="Reward">Reward</option>
              <option value="Achievement">Achievement</option>
              <option value="Special">Special</option>
            </select>
            <select
              className="w-full px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value)}
            >
              <option value="all">All Rarities</option>
              <option value="Common">Common</option>
              <option value="Rare">Rare</option>
              <option value="Epic">Epic</option>
              <option value="Legendary">Legendary</option>
            </select>
          </div>
        </div>

        {/* Cards Table */}
        <div className="bg-gray-800/30 backdrop-blur-md rounded-xl shadow-2xl overflow-hidden border border-gray-700/50">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/50">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Card Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Rarity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Times Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-300 uppercase tracking-wider">Streamer</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-red-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800/20 divide-y divide-gray-700/30">
                {currentCards.map(card => (
                  <tr key={card._id} className="hover:bg-gray-700/30 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{card.cardTitle}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{card.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <img 
                        src={card.imageUrl} 
                        alt={card.cardTitle}
                        className="h-10 w-10 rounded object-cover border border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {card.category.map((cat, index) => (
                          <span key={index} className="inline-block bg-red-500/20 rounded-full px-3 py-1 text-xs font-semibold text-red-300 mr-2 border border-red-500/30">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                        card.rarity === 'Epic' ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' :
                        card.rarity === 'Rare' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                        card.rarity === 'Legendary' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                        card.rarity === 'Mythic' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                        'bg-gray-500/20 text-gray-300 border-gray-500/30'
                      }`}>
                        {card.rarity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white font-medium">${card.price}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{card.timesUsed || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{card.streamerEmailId || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDeleteClick(card)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          <div className="px-6 py-4 border-t border-gray-700/50 bg-gray-800/30">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Items per page selector */}
              <div className="flex items-center">
                <span className="text-sm text-gray-300 mr-2">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-600 bg-gray-800/50 text-white rounded px-2 py-1 text-sm backdrop-blur-sm focus:ring-2 focus:ring-red-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-300 ml-2">entries</span>
              </div>
              
              {/* Page info and navigation */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="text-sm text-gray-300">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                </span>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded text-sm transition-all duration-200 ${
                      currentPage === 1
                        ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded text-sm transition-all duration-200 ${
                      currentPage === 1
                        ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 rounded text-sm transition-all duration-200 ${
                            currentPage === pageNum
                              ? 'bg-red-600 text-white shadow-lg shadow-red-500/25'
                              : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded text-sm transition-all duration-200 ${
                      currentPage === totalPages
                        ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => paginate(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded text-sm transition-all duration-200 ${
                      currentPage === totalPages
                        ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/25'
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Card"
        message={`Are you sure you want to delete ${cardToDelete?.name}? This action cannot be undone.`}
      />
    </div>
  );
};

export default CardManagement;